import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './tag-modal.html';
import type { Tag, TagGroup } from '../services/api.types';
import { createTag, updateTag } from '../services/api.service';
import { DEFAULT_TAG_COLOR, TAG_COLOR_OPTIONS, type TagColorOption } from '../services/tag-colors';

@customElement({ name: 'tag-modal', template })
export class TagModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (tag: Tag) => void;
  @bindable public closed!: () => void;
  /** When provided the modal operates in edit mode */
  @bindable public tagToEdit: Tag | null = null;
  @bindable public groups: TagGroup[] = [];

  public name = '';
  public color = DEFAULT_TAG_COLOR;
  public searchText = '';
  public groupId = '';
  public saving = false;
  public error = '';
  public colorMenuOpen = false;
  public colorPickerEl: HTMLElement | null = null;
  public readonly colorOptions = TAG_COLOR_OPTIONS;

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean {
    return this.tagToEdit !== null;
  }

  public attached(): void {
    this.color = DEFAULT_TAG_COLOR;
    this.colorMenuOpen = false;
    if (this.tagToEdit) {
      this.name = this.tagToEdit.name;
      this.color = this.tagToEdit.color || DEFAULT_TAG_COLOR;
      this.searchText = this.tagToEdit.searchText ?? '';
      this.groupId = this.tagToEdit.groupId ?? '';
    }
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (this.colorMenuOpen) {
        this.colorMenuOpen = false;
        e.stopPropagation();
        return;
      }
      this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public close(): void {
    this.closed();
  }

  public get colorOptionsWithCurrent(): TagColorOption[] {
    if (!this.color || this.colorOptions.some(option => option.value === this.color)) {
      return [...this.colorOptions];
    }
    return [{ key: 'current', value: this.color }, ...this.colorOptions];
  }

  public colorLabel(option: TagColorOption): string {
    return this.i18n.tr(`tags.colors.${option.key}`);
  }

  public toggleColorMenu(event: Event): void {
    event.stopPropagation();
    this.colorMenuOpen = !this.colorMenuOpen;
  }

  public selectColor(value: string): void {
    this.color = value;
    this.colorMenuOpen = false;
  }

  public onModalClick(event: Event): void {
    if (!this.colorMenuOpen || !this.colorPickerEl) return;
    if (!this.colorPickerEl.contains(event.target as Node)) this.colorMenuOpen = false;
  }

  public async save(): Promise<void> {
    if (!this.name.trim()) {
      this.error = 'Name is required.';
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      let tag: Tag;
      if (this.isEditMode && this.tagToEdit) {
        tag = await updateTag(this.tagToEdit.id, {
          name: this.name.trim(),
          color: this.color,
          searchText: this.searchText.trim(),
          groupId: this.groupId || null,
        });
      } else {
        tag = await createTag({
          name: this.name.trim(),
          color: this.color,
          searchText: this.searchText.trim(),
          groupId: this.groupId || null,
        });
      }
      this.saved(tag);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
}
